import { useMutation, useQueryClient } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";
import { CASINO_CONFIGURATION_GAMES_SCOPE_QUERY_KEY } from "./casinoConfigurationGamesQueryKeys";

/** Server enforces a hard cap; mirror it client-side to fail fast before the request. */
export const CASINO_BULK_ACTIVE_BY_IDS_MAX = 500;

export type CasinoBulkGameIdentity = {
  provider: string;
  game_id: string;
};

export type CasinoBulkActiveByIdsVariables = {
  is_active: boolean;
  games: CasinoBulkGameIdentity[];
};

export type CasinoBulkActiveByIdsResult = {
  /** Deduped count of identities the server accepted. */
  requested: number;
  /** Rows whose is_active actually changed (no-op flips excluded). */
  affected: number;
  is_active: boolean;
};

export function useCasinoConfigurationBulkActiveGamesByIds() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      variables: CasinoBulkActiveByIdsVariables,
    ): Promise<CasinoBulkActiveByIdsResult> => {
      const token = JSON.parse(localStorage.getItem("token") ?? "null");
      const instance = createPrivateAdminPanelInstance(token?.AccessToken);
      const { data: responseBody } = await instance.post(
        API_ROUTES.BULK_ACTIVE_GAMES_BY_IDS,
        { is_active: variables.is_active, games: variables.games },
      );
      if (responseBody?.success === false) {
        throw new Error(responseBody?.message || "Bulk update rejected");
      }
      const data = responseBody?.data ?? {};
      return {
        requested: Number(data.requested ?? variables.games.length),
        affected: Number(data.affected ?? 0),
        is_active: Boolean(data.is_active ?? variables.is_active),
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: CASINO_CONFIGURATION_GAMES_SCOPE_QUERY_KEY,
      });
    },
  });
}
